/*
 * <go-dot-slider> — gemeinsame Slider-Logik für die go-Sektionen.
 *
 * Ersetzt drei nahezu identische IntersectionObserver-IIFEs
 * (go-bestsellers, go-featured-collection, go-testimonials).
 *
 * Erwartetes Markup (Klassen bleiben sektionsspezifisch):
 *   <go-dot-slider data-dot-class="go-x__dot" data-slide-label="Produkt">
 *     <ul data-slider-track> <li>…</li> … </ul>
 *     <div data-slider-dots></div>            (optional — Dots werden generiert)
 *     <button data-slider-prev>…</button>     (optional)
 *     <button data-slider-next>…</button>     (optional)
 *   </go-dot-slider>
 *
 * A11y: Track wird als fokussierbares Karussell ausgezeichnet, Pfeiltasten
 * navigieren, Scroll-Verhalten respektiert prefers-reduced-motion.
 *
 * Wird per `defer` geladen → Kinder sind beim connectedCallback geparst.
 * Re-Init im Theme-Editor passiert automatisch (neues Element = neuer Callback).
 */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class GoDotSlider extends HTMLElement {
    connectedCallback() {
      if (this.slides) return; // bereits initialisiert

      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;

      var slides = Array.prototype.filter.call(this.track.children, function (el) {
        return el.nodeType === 1;
      });
      if (slides.length < 2) return; // nichts zu sliden
      this.slides = slides;

      this.dotsContainer = this.querySelector('[data-slider-dots]');
      this.prevBtn = this.querySelector('[data-slider-prev]');
      this.nextBtn = this.querySelector('[data-slider-next]');
      this.dotClass = this.getAttribute('data-dot-class') || 'go-dot-slider__dot';
      this.slideLabel = this.getAttribute('data-slide-label') || 'Slide';
      this.fade = this.hasAttribute('data-fade');
      this.activeIndex = 0;

      this._setupRegion();
      this._buildDots();
      if (this.fade) {
        this._initFade();
      } else {
        this._observe();
      }
      this._wireControls();
      this._setActive(0);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    _setupRegion() {
      if (!this.track.hasAttribute('tabindex')) this.track.setAttribute('tabindex', '0');
      // Keep the track's native list semantics for its li children.
      this.setAttribute('role', 'group');
      this.setAttribute('aria-roledescription', 'Karussell');
      if (!this.hasAttribute('aria-label')) {
        this.setAttribute(
          'aria-label',
          this.slideLabel + '-Slider, ' + this.slides.length + ' Einträge — mit den Pfeiltasten navigieren'
        );
      }
    }

    _buildDots() {
      this.dots = [];
      if (!this.dotsContainer) return;
      this.dotsContainer.replaceChildren();
      var self = this;
      this.slides.forEach(function () {
        var dot = document.createElement('span');
        dot.className = self.dotClass;
        dot.setAttribute('data-active', 'false');
        self.dotsContainer.appendChild(dot);
        self.dots.push(dot);
      });
    }

    _setActive(idx) {
      this.activeIndex = idx;
      this.dots.forEach(function (d, i) {
        var on = i === idx;
        d.classList.toggle('is-active', on);
        d.setAttribute('data-active', on ? 'true' : 'false');
      });
      if (this.prevBtn) this.prevBtn.disabled = idx <= 0;
      if (this.nextBtn) this.nextBtn.disabled = idx >= this.slides.length - 1;
    }

    goTo(idx) {
      if (idx < 0 || idx >= this.slides.length) return;
      if (this.fade) {
        this._showFade(idx);
        return;
      }
      this.slides[idx].scrollIntoView({
        behavior: REDUCED.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }

    /* Fade-Modus: Slides liegen übereinander, Wechsel per Crossfade statt Scroll. */
    _initFade() {
      var self = this;
      this.track.setAttribute('data-fade-track', '');
      this.slides.forEach(function (s, i) {
        s.classList.toggle('is-shown', i === 0);
        s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
        s.inert = i !== 0;
      });
      // Touch-Swipe bleibt nutzbar (Mobile): horizontale Geste wechselt den Slide.
      var startX = null;
      this.track.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });
      this.track.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        startX = null;
        if (Math.abs(dx) < 40) return;
        self.goTo(self.activeIndex + (dx < 0 ? 1 : -1));
      }, { passive: true });
    }

    _showFade(idx) {
      this.slides.forEach(function (s, i) {
        s.classList.toggle('is-shown', i === idx);
        s.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
        s.inert = i !== idx;
      });
      this._setActive(idx);
    }

    _observe() {
      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
              var idx = self.slides.indexOf(entry.target);
              if (idx !== -1) self._setActive(idx);
            }
          });
        },
        { root: this.track, threshold: [0.55, 0.8] }
      );
      this.slides.forEach(function (s) {
        self.observer.observe(s);
      });
    }

    _wireControls() {
      var self = this;
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          self.goTo(self.activeIndex - 1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          self.goTo(self.activeIndex + 1);
        });
      }
      this.track.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          self.goTo(self.activeIndex + 1);
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          self.goTo(self.activeIndex - 1);
        }
      });
    }
  }

  if (!customElements.get('go-dot-slider')) {
    customElements.define('go-dot-slider', GoDotSlider);
  }
})();
